import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { FolderClosed, FolderOpen, Loader2 } from 'lucide-react'
import {
  useFolders,
  useProjects,
  useUpdateDocumentLocation,
  type FolderRead,
} from '@/api/queries'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { formatError } from '@/lib/format-error'

/**
 * Move-document dialog used from the project doc list. Lets the user pick
 * a different project (within the same workspace) and optionally a folder
 * inside it. Backend rejects cross-workspace moves; the UI mirrors that by
 * scoping the project picker to ``workspaceUuid``.
 *
 * The dialog DOES NOT support moving between workspaces in MVP — that
 * needs a re-share flow on the document side (`useCreateDocumentShare`),
 * which is the work item tracked separately.
 */
interface MoveDocumentDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  documentId: string
  documentName: string
  workspaceUuid: string
  currentProjectUuid: string | null
  currentFolderUuid: string | null
  onMoveStateChange?: (
    documentId: string,
    state: 'idle' | 'pending' | 'settled',
  ) => void
}

export function MoveDocumentDialog(props: MoveDocumentDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      {/* Inner form re-mounts every open so initial state is always
          re-seeded from props — no useEffect-driven reset needed. */}
      {props.open && <MoveDocumentDialogForm {...props} />}
    </Dialog>
  )
}

function MoveDocumentDialogForm({
  onOpenChange,
  documentId,
  documentName,
  workspaceUuid,
  currentProjectUuid,
  currentFolderUuid,
  onMoveStateChange,
}: MoveDocumentDialogProps) {
  const { data: projects, isLoading: projectsLoading } = useProjects(workspaceUuid)
  const [selectedProject, setSelectedProject] = useState<string | null>(
    currentProjectUuid,
  )
  const [selectedFolder, setSelectedFolder] = useState<string | null>(
    currentFolderUuid,
  )

  // When the user picks a different project, reset the folder choice so we
  // don't carry over a folder that belongs to a different project.
  const handleProjectChange = (next: string | null) => {
    setSelectedProject(next)
    setSelectedFolder(null)
  }

  const moveMutation = useUpdateDocumentLocation(documentId, workspaceUuid)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Compute the body: only include keys that actually changed so the
    // backend's ``model_fields_set`` semantics short-circuit no-ops.
    const body: {
      project_uuid?: string | null
      folder_uuid?: string | null
    } = {}
    if (selectedProject !== currentProjectUuid) {
      body.project_uuid = selectedProject
    }
    if (selectedFolder !== currentFolderUuid) {
      body.folder_uuid = selectedFolder
    }
    if (Object.keys(body).length === 0) {
      onOpenChange(false)
      return
    }
    try {
      onMoveStateChange?.(documentId, 'pending')
      await moveMutation.mutateAsync(body)
      toast.success(`Moved "${documentName}"`)
      onMoveStateChange?.(documentId, 'settled')
      onOpenChange(false)
    } catch (err) {
      toast.error(formatError(err))
      onMoveStateChange?.(documentId, 'idle')
    }
  }

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Move &quot;{documentName}&quot;</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="move-project">Project</Label>
          <ProjectPicker
            projects={projects ?? []}
            loading={projectsLoading}
            selected={selectedProject}
            onChange={handleProjectChange}
          />
        </div>
        {selectedProject && (
          <div className="space-y-1.5">
            <Label htmlFor="move-folder">Folder</Label>
            <FolderPicker
              workspaceUuid={workspaceUuid}
              projectUuid={selectedProject}
              selected={selectedFolder}
              onChange={setSelectedFolder}
            />
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={moveMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={moveMutation.isPending}>
            {moveMutation.isPending && <Loader2 className="animate-spin" />}
            Move
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}

function ProjectPicker({
  projects,
  loading,
  selected,
  onChange,
}: {
  projects: { uuid: string; name: string }[]
  loading: boolean
  selected: string | null
  onChange: (next: string | null) => void
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        Loading projects…
      </div>
    )
  }
  if (projects.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        This workspace has no projects yet. Create one before moving.
      </p>
    )
  }
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => onChange(null)}
        className={cn(
          'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-sm',
          'transition-colors hover:bg-muted/60',
          selected === null
            ? 'border-primary bg-primary-soft/60 text-primary-soft-foreground'
            : 'border-border bg-card',
        )}
      >
        <FolderClosed className="size-4 text-muted-foreground" aria-hidden />
        <span className="flex-1 text-left">
          No project (workspace root)
        </span>
        {selected === null && (
          <span className="text-xs font-medium text-primary">Selected</span>
        )}
      </button>
      {projects.map((p) => (
        <button
          key={p.uuid}
          type="button"
          onClick={() => onChange(p.uuid)}
          className={cn(
            'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-sm',
            'transition-colors hover:bg-muted/60',
            selected === p.uuid
              ? 'border-primary bg-primary-soft/60 text-primary-soft-foreground'
              : 'border-border bg-card',
          )}
        >
          <FolderOpen className="size-4 text-muted-foreground" aria-hidden />
          <span className="flex-1 truncate text-left">{p.name}</span>
          {selected === p.uuid && (
            <span className="text-xs font-medium text-primary">Selected</span>
          )}
        </button>
      ))}
    </div>
  )
}

function FolderPicker({
  workspaceUuid,
  projectUuid,
  selected,
  onChange,
}: {
  workspaceUuid: string
  projectUuid: string
  selected: string | null
  onChange: (next: string | null) => void
}) {
  const { data: folders, isLoading } = useFolders(workspaceUuid, projectUuid)

  // Render folders as a flat indented list — easier than nesting buttons.
  const flat = useMemo(() => flattenFolders(folders ?? []), [folders])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        Loading folders…
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => onChange(null)}
        className={cn(
          'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-sm',
          'transition-colors hover:bg-muted/60',
          selected === null
            ? 'border-primary bg-primary-soft/60 text-primary-soft-foreground'
            : 'border-border bg-card',
        )}
      >
        <FolderClosed className="size-4 text-muted-foreground" aria-hidden />
        <span className="flex-1 text-left">Project root (no folder)</span>
      </button>
      {flat.map((f) => (
        <button
          key={f.folder.uuid}
          type="button"
          onClick={() => onChange(f.folder.uuid)}
          className={cn(
            'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-sm',
            'transition-colors hover:bg-muted/60',
            selected === f.folder.uuid
              ? 'border-primary bg-primary-soft/60 text-primary-soft-foreground'
              : 'border-border bg-card',
          )}
          style={{ paddingLeft: `${f.depth * 14 + 12}px` }}
        >
          <FolderOpen className="size-4 text-muted-foreground" aria-hidden />
          <span className="flex-1 truncate text-left">{f.folder.name}</span>
        </button>
      ))}
    </div>
  )
}

function flattenFolders(
  folders: FolderRead[],
): { folder: FolderRead; depth: number }[] {
  const byParent = new Map<string | null, FolderRead[]>()
  for (const f of folders) {
    const key = f.parent_folder_uuid ?? null
    const list = byParent.get(key) ?? []
    list.push(f)
    byParent.set(key, list)
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name))
  }
  const out: { folder: FolderRead; depth: number }[] = []
  const visit = (parent: string | null, depth: number) => {
    for (const f of byParent.get(parent) ?? []) {
      out.push({ folder: f, depth })
      visit(f.uuid, depth + 1)
    }
  }
  visit(null, 0)
  return out
}
